import React, {FormEvent, useState} from "react"
import {useTranslation} from "react-i18next"
import {alpha, Button as MuiButton, Box, Stack, TextField, Typography} from "@mui/material"
import {isValidEmail, Participant} from "../../library/Participant"
import Theme from "../../library/Theme"
import "react-phone-number-input/style.css"
import PhoneInput, {isValidPhoneNumber} from "react-phone-number-input"
import BigButton from "../buttons/BigButton"
import {floatingInputSx} from "../../styles/inputStyles"

export function PrivacyPolicyLink({textColor}: { textColor: string }) {
  const {t} = useTranslation()
  const color = alpha(textColor, 0.8)
  return (
    <MuiButton
      variant="text"
      size="small"
      href="https://www.simplebooth.com/privacy-policy"
      target="_blank"
      rel="noopener noreferrer"
      sx={{mt: 2, fontSize: 14, color, "&:hover": {color, textDecoration: "underline"}}}
    >
      {t("splash.privacyPolicy")}
    </MuiButton>
  )
}

interface SplashFormProps {
  theme: Theme
  currentParticipant: Participant
  emailOn?: boolean
  firstNameOn?: boolean
  lastNameOn?: boolean
  phoneNumberOn?: boolean
  handleSuccess: (participant: Participant) => void
}

const phoneInputContainerSx = {
  "& .PhoneInputInput": {
    border: "none",
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    boxShadow: "0 3px 10px rgb(0 0 0 / 15%)",
    marginLeft: "5px",
    color: "#495057",
    height: "50px",
    paddingLeft: "20px",
    fontSize: "1rem",
    "&:focus": {
      outline: "none",
    },
    "&::placeholder": {
      color: "#999",
    },
  },
  "& .PhoneInputCountry": {
    border: "none",
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    boxShadow: "0 3px 10px rgb(0 0 0 / 15%)",
    padding: "0 12px",
  },
}

export default function SplashForm({
                                     theme,
                                     currentParticipant,
                                     emailOn,
                                     firstNameOn,
                                     lastNameOn,
                                     phoneNumberOn,
                                     handleSuccess,
                                   }: SplashFormProps) {
  const {t} = useTranslation()

  const [email, setEmail] = useState<string | null>(currentParticipant.email)
  const [firstName, setFirstName] = useState<string | null>(currentParticipant.firstName)
  const [lastName, setLastName] = useState<string | null>(currentParticipant.lastName)
  const [phoneNumber, setPhoneNumber] = useState<string | null>(currentParticipant.phoneNumber)
  const [phoneNumberCountry, setPhoneNumberCountry] = useState<string>("US")

  // Derive validity directly from state
  const emailValid = email ? isValidEmail(email) : false
  const firstNameValid = firstName ? firstName.length > 0 : false
  const lastNameValid = lastName ? lastName.length > 0 : false
  const phoneNumberValid = phoneNumber ? isValidPhoneNumber(phoneNumber) : false

  const formValid =
    (emailValid || !emailOn) &&
    (firstNameValid || !firstNameOn) &&
    (lastNameValid || !lastNameOn) &&
    (phoneNumberValid || !phoneNumberOn)

  const handleEmailChange = (value: string) => {
    setEmail(String(value).toLowerCase())
  }

  const handlePhoneChange = (value?: string) => {
    if (!value) return
    setPhoneNumber(value)
  }

  const submit = (e?: FormEvent) => {
    e?.preventDefault()

    if (!formValid) return

    const participant: Participant = {
      ...currentParticipant,
      email: email ?? currentParticipant.email,
      firstName: firstName ?? currentParticipant.firstName,
      lastName: lastName ?? currentParticipant.lastName,
      phoneNumber: phoneNumber ?? currentParticipant.phoneNumber,
      acceptedTerms: new Date(),
    }
    handleSuccess(participant)
  }

  const showFirstName = firstNameOn
  const showLastName = lastNameOn
  const showPhoneNumber = phoneNumberOn
  const showEmail = emailOn

  const showForm = showEmail || showFirstName || showLastName || showPhoneNumber
  if (!showForm) {
    return null
  }

  const emailOnly = emailOn && !firstNameOn && !lastNameOn && !phoneNumberOn
  if (emailOnly && !currentParticipant.email) {
    return (
      <Box component="form" onSubmit={submit}>
        <Stack direction="row" spacing={1} alignItems="stretch">
          <TextField
            type="email"
            name="email"
            value={email ?? ""}
            placeholder={t("splash.emailPlaceholder")}
            aria-label="Email"
            onChange={(e) => handleEmailChange(e.target.value)}
            required
            autoComplete="email"
            fullWidth
            sx={floatingInputSx}
          />
          <BigButton
            size="small"
            type="submit"
            disabled={!formValid}
          >
            {t("splash.continue")}
          </BigButton>
        </Stack>
      </Box>
    )
  }

  // One or more data capture fields besides email
  return (
    <Box component="form" onSubmit={submit}>
      {(showFirstName || showLastName) && (
        <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
          {showFirstName && (
            <Box sx={{flex: 1}}>
              <Typography sx={{mb: 0.5, fontWeight: 500, textAlign: "left", color: theme.textColor}}>
                {t("splash.firstName")}
              </Typography>
              <TextField
                type="text"
                name="firstName"
                value={firstName ?? ""}
                placeholder={t("splash.firstNamePlaceholder")}
                aria-label="First name"
                onChange={(e) => setFirstName(e.target.value)}
                required

                autoComplete="given-name"
                fullWidth
                sx={floatingInputSx}
              />
            </Box>
          )}
          {showLastName && (
            <Box sx={{flex: 1}}>
              <Typography sx={{mb: 0.5, fontWeight: 500, textAlign: "left", color: theme.textColor}}>
                {t("splash.lastName")}
              </Typography>
              <TextField
                type="text"
                name="lastName"
                value={lastName ?? ""}
                placeholder={t("splash.lastNamePlaceholder")}
                aria-label="Last name"
                onChange={(e) => setLastName(e.target.value)}
                required

                autoComplete="family-name"
                fullWidth
                sx={floatingInputSx}
              />
            </Box>
          )}
        </Stack>
      )}

      {showEmail && (
        <Box sx={{mt: 2.5}}>
          <Typography sx={{mb: 0.5, fontWeight: 500, textAlign: "left", color: theme.textColor}}>
            {t("splash.emailAddress")}
          </Typography>
          <TextField
            type="email"
            name="email"
            value={email ?? ""}
            placeholder={t("splash.emailPlaceholderAlternate")}
            aria-label="Email"
            onChange={(e) => handleEmailChange(e.target.value)}
            required

            autoComplete="email"
            fullWidth
            sx={floatingInputSx}
          />
        </Box>
      )}

      {showPhoneNumber && (
        <Box sx={{mt: 2.5}}>
          <Typography sx={{mb: 0.5, fontWeight: 500, textAlign: "left", color: theme.textColor}}>
            {t("splash.phoneNumber")}
          </Typography>
          <Box sx={phoneInputContainerSx}>
            <PhoneInput
              country={phoneNumberCountry}
              placeholder={phoneNumberCountry === "US" ? t("splash.phoneNumberPlaceholder.US") : ""}
              value={phoneNumber ?? ""}
  
              onChange={handlePhoneChange}
              onCountryChange={(countryCode) => setPhoneNumberCountry(countryCode ?? "US")}
            />
          </Box>
        </Box>
      )}

      <BigButton
        type="submit"
        disabled={!formValid}
        sx={{mt: 3, width: "100%"}}
      >
        {t("splash.continue")}
      </BigButton>
      <PrivacyPolicyLink textColor={theme.textColor} />
    </Box>
  )
}
